import { useId, type ReactNode } from 'react';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Line, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';
import { ArrowDownRight, ArrowUpRight, Minus } from 'lucide-react';
import { cn } from '@/lib/utils';

/** Report palette (see --adm-c* in admin.css; validated for colour-blind separation). */
export const CHART = {
  violet: 'var(--adm-c1)',
  teal: 'var(--adm-c2)',
  amber: 'var(--adm-c3)',
  blue: 'var(--adm-c4)',
  pink: 'var(--adm-c5)',
  prev: 'var(--adm-c-prev)',
} as const;

const AXIS_TICK = { fontSize: 11, fill: 'hsl(var(--muted-foreground))' };

// ---------------------------------------------------------------------------
// Delta pill
// ---------------------------------------------------------------------------

export function Delta({
  value,
  unit = '%',
  className,
}: {
  /** Change to show; null hides the pill. */
  value: number | null;
  /** '%' for relative change, 'pts' for percentage-point change. */
  unit?: '%' | 'pts';
  className?: string;
}) {
  if (value === null || !Number.isFinite(value)) return null;
  const rounded = Math.round(value);
  const tone = rounded > 0 ? 'up' : rounded < 0 ? 'down' : 'flat';
  const Icon = tone === 'up' ? ArrowUpRight : tone === 'down' ? ArrowDownRight : Minus;
  return (
    <span className={cn('adm-delta adm-num', `adm-delta--${tone}`, className)}>
      <Icon className="h-3 w-3" aria-hidden="true" />
      {rounded > 0 ? '+' : ''}
      {rounded}
      {unit === '%' ? '%' : ' pts'}
      <span className="sr-only">{tone === 'up' ? 'up' : tone === 'down' ? 'down' : 'no change'} on previous period</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Tooltip shell
// ---------------------------------------------------------------------------

export function ChartTip({ children }: { children: ReactNode }) {
  return (
    <div className="min-w-[150px] rounded-lg border border-border bg-popover px-3 py-2 text-[12px] text-popover-foreground shadow-[var(--adm-shadow-md)]">
      {children}
    </div>
  );
}

export function TipRow({ color, dashed, label, value }: { color: string; dashed?: boolean; label: ReactNode; value: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-4 py-0.5">
      <span className="flex items-center gap-1.5 text-muted-foreground">
        <LegendMark color={color} dashed={dashed} />
        {label}
      </span>
      <span className="adm-num font-semibold text-foreground">{value}</span>
    </div>
  );
}

export function LegendMark({ color, dashed, shape = 'line' }: { color: string; dashed?: boolean; shape?: 'line' | 'square' }) {
  if (shape === 'square') {
    return <span aria-hidden="true" className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{ background: color }} />;
  }
  return (
    <svg aria-hidden="true" width="14" height="6" className="shrink-0">
      <line x1="1" y1="3" x2="13" y2="3" stroke={color} strokeWidth="2" strokeLinecap="round" strokeDasharray={dashed ? '3 3' : undefined} />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Current vs previous period line chart (Fikri "Transaction" card)
// ---------------------------------------------------------------------------

export interface ComparisonDatum {
  label: string;
  tooltip: string;
  prevTooltip: string;
  current: number;
  previous: number;
}

export function ComparisonChart({
  data,
  color,
  format,
  axisFormat,
  height = 190,
  currentLabel = 'This period',
  variant = 'line',
}: {
  data: ComparisonDatum[];
  color: string;
  format: (value: number) => string;
  axisFormat?: (value: number) => string;
  height?: number;
  currentLabel?: string;
  /** 'bars' suits many daily buckets (closed days drop to zero without a zigzag). */
  variant?: 'line' | 'bars';
}) {
  return (
    <div style={{ height }}>
      <ResponsiveContainer width="100%" height="100%">
        <ComposedChart data={data} margin={{ top: 8, right: 4, bottom: 0, left: 4 }} barCategoryGap="28%">
          <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="0" />
          <XAxis
            dataKey="label"
            axisLine={false}
            tickLine={false}
            tick={AXIS_TICK}
            interval="preserveStartEnd"
            minTickGap={18}
            tickMargin={8}
          />
          <YAxis
            orientation="right"
            axisLine={false}
            tickLine={false}
            tick={AXIS_TICK}
            width={48}
            allowDecimals={false}
            tickFormatter={(v) => (axisFormat ?? format)(Number(v))}
          />
          <Tooltip
            cursor={
              variant === 'bars'
                ? { fill: 'hsl(var(--muted))' }
                : { stroke: 'hsl(var(--muted-foreground) / 0.35)', strokeWidth: 1 }
            }
            content={({ active, payload }) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload as ComparisonDatum;
              return (
                <ChartTip>
                  <TipRow color={color} label={d.tooltip} value={format(d.current)} />
                  <TipRow color={CHART.prev} dashed label={d.prevTooltip} value={format(d.previous)} />
                </ChartTip>
              );
            }}
          />
          {variant === 'bars' && (
            <Bar name={currentLabel} dataKey="current" fill={color} radius={[3, 3, 0, 0]} maxBarSize={18} animationDuration={600} />
          )}
          <Line
            name="Previous period"
            type="monotone"
            dataKey="previous"
            stroke={CHART.prev}
            strokeWidth={1.5}
            strokeDasharray="4 4"
            dot={false}
            activeDot={false}
            isAnimationActive={false}
          />
          {variant === 'line' && (
          <Line
            name={currentLabel}
            type="monotone"
            dataKey="current"
            stroke={color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4.5, fill: color, stroke: 'hsl(var(--card))', strokeWidth: 2 }}
            animationDuration={700}
          />
          )}
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Semicircle gauge (Fikri "Learning content" / "Candidate sources")
// ---------------------------------------------------------------------------

export interface GaugeSegment {
  key: string;
  label: string;
  value: number;
  color: string;
}

export function GaugeChart({
  segments,
  total,
  caption,
  format = (v: number) => String(v),
}: {
  segments: GaugeSegment[];
  total: number;
  caption: string;
  format?: (value: number) => string;
}) {
  const shown = segments.filter((s) => s.value > 0);
  const empty = shown.length === 0;
  return (
    <div className="relative mx-auto h-[150px] w-full max-w-[280px]">
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={empty ? [{ key: 'empty', label: 'No data', value: 1, color: 'hsl(var(--muted))' }] : shown}
            dataKey="value"
            nameKey="label"
            cx="50%"
            cy="88%"
            startAngle={180}
            endAngle={0}
            innerRadius="122%"
            outerRadius="150%"
            paddingAngle={empty || shown.length === 1 ? 0 : 1.5}
            cornerRadius={4}
            stroke="hsl(var(--card))"
            strokeWidth={2}
            isAnimationActive={!empty}
            animationDuration={700}
          >
            {(empty ? [{ color: 'hsl(var(--muted))', key: 'empty' }] : shown).map((s) => (
              <Cell key={s.key} fill={s.color} />
            ))}
          </Pie>
          {!empty && (
            <Tooltip
              content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const s = payload[0].payload as GaugeSegment;
                return (
                  <ChartTip>
                    <TipRow
                      color={s.color}
                      label={s.label}
                      value={`${format(s.value)} · ${total ? Math.round((s.value / total) * 100) : 0}%`}
                    />
                  </ChartTip>
                );
              }}
            />
          )}
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-x-0 bottom-1 text-center">
        <p className="text-[11.5px] text-muted-foreground">{caption}</p>
        <p className="adm-num text-[30px] font-semibold leading-tight tracking-tight text-foreground">{format(total)}</p>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Single-series column chart (Fikri "Length of service")
// ---------------------------------------------------------------------------

export function ColumnChart<T extends Record<string, unknown>>({
  data,
  xKey,
  yKey,
  color,
  height = 180,
  tooltip,
  highlightMax = true,
}: {
  data: T[];
  xKey: keyof T & string;
  yKey: keyof T & string;
  color: string;
  height?: number;
  tooltip: (row: T) => ReactNode;
  highlightMax?: boolean;
}) {
  const id = useId();
  const max = Math.max(0, ...data.map((d) => Number(d[yKey]) || 0));
  return (
    <div style={{ height }} id={id}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={data} margin={{ top: 8, right: 0, bottom: 0, left: -18 }} barCategoryGap="24%">
          <CartesianGrid vertical={false} stroke="hsl(var(--border))" />
          <XAxis dataKey={xKey} axisLine={false} tickLine={false} tick={AXIS_TICK} tickMargin={8} />
          <YAxis allowDecimals={false} axisLine={false} tickLine={false} tick={AXIS_TICK} />
          <Tooltip
            cursor={{ fill: 'hsl(var(--muted))', radius: 6 }}
            content={({ active, payload }) => (active && payload?.length ? <ChartTip>{tooltip(payload[0].payload as T)}</ChartTip> : null)}
          />
          <Bar dataKey={yKey} radius={[4, 4, 0, 0]} animationDuration={600} maxBarSize={44}>
            {data.map((d, i) => (
              <Cell
                key={i}
                fill={color}
                fillOpacity={!highlightMax || max === 0 || Number(d[yKey]) === max ? 1 : 0.45}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
